import dayjs, { type Dayjs } from 'dayjs';

export const LISTING_FORM_INITIAL_VALUES = {
  listingType: 'product' as const,
  itemType: 'new' as const,
  currency: 'INR',
  priceType: 'negotiable' as const,
  showPrice: true,
  status: 'draft' as const,
  showExactLocation: false,
  warrantyAvailable: false,
  tags: [] as string[],
  keywords: [] as string[],
  serviceArea: [] as string[],
};

export const PRICE_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'negotiable', label: 'Negotiable' },
  { value: 'starting_from', label: 'Starting from' },
  { value: 'per_unit', label: 'Per unit' },
  { value: 'per_hour', label: 'Per hour' },
  { value: 'per_day', label: 'Per day' },
  { value: 'on_request', label: 'On request' },
];

export const CURRENCY_OPTIONS = [
  { value: 'INR', label: 'INR' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

export const ITEM_TYPE_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'refurbished', label: 'Refurbished' },
  { value: 'used', label: 'Used' },
  { value: 'scrap', label: 'Scrap' },
];

export const LISTING_TYPE_OPTIONS = [
  { value: 'product', label: 'Product' },
  { value: 'service', label: 'Service' },
];

export const PRODUCT_CONDITION_OPTIONS = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'for_parts', label: 'For parts' },
];

export const AVAILABILITY_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'within_24hrs', label: 'Within 24 hours' },
  { value: 'within_week', label: 'Within a week' },
  { value: 'scheduled', label: 'Scheduled' },
];

export const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function expiresToIso(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (dayjs.isDayjs(value)) return (value as Dayjs).toISOString();
  if (typeof value === 'string') return value;
  return undefined;
}

/** Same payload shape as admin `ListingManagement` handleSubmit (create). */
export function buildCreateListingBody(values: Record<string, unknown>): Record<string, unknown> {
  const listingType = values.listingType as 'product' | 'service';
  return {
    listingType,
    itemType: values.itemType as 'new' | 'refurbished' | 'used' | 'scrap',
    title: values.title as string,
    description: values.description as string,
    category: values.category as string,
    subcategory: (values.subcategory as string) || undefined,
    storeId: values.store as string,
    price: {
      amount: Number(values.price),
      currency: ((values.currency as string) || 'INR') as string,
      priceType:
        ((values.priceType as string) || 'negotiable') as
          | 'fixed'
          | 'negotiable'
          | 'starting_from'
          | 'per_unit'
          | 'per_hour'
          | 'per_day'
          | 'on_request',
      showPrice: values.showPrice !== false,
    },
    productDetails:
      listingType === 'product'
        ? {
            brand: values.brand as string | undefined,
            model: values.model as string | undefined,
            condition: values.condition as 'excellent' | 'good' | 'fair' | 'poor' | 'for_parts',
            yearOfPurchase: values.yearOfPurchase != null ? Number(values.yearOfPurchase) : undefined,
            warranty: {
              available: Boolean(values.warrantyAvailable),
              duration: values.warrantyDuration as string | undefined,
              details: values.warrantyDetails as string | undefined,
            },
            specifications: (values.specifications as Array<{ key: string; value: string }>) || [],
          }
        : undefined,
    serviceDetails:
      listingType === 'service'
        ? {
            serviceType: values.serviceType as string,
            duration: values.duration as string | undefined,
            availability: values.availability as
              | 'immediate'
              | 'within_24hrs'
              | 'within_week'
              | 'scheduled',
            serviceArea: (values.serviceArea as string[]) || [],
          }
        : undefined,
    location: {
      address: values.address as string | undefined,
      city: values.city as string | undefined,
      state: values.state as string | undefined,
      zipCode: values.zipCode as string | undefined,
      coordinates: values.coordinates as number[] | undefined,
      showExactLocation: Boolean(values.showExactLocation),
    },
    tags: (values.tags as string[]) || [],
    status: ((values.status as string) || 'draft') as 'draft' | 'active' | 'inactive',
    expiresAt: expiresToIso(values.expiresAt),
    seo: {
      metaTitle: values.metaTitle as string | undefined,
      metaDescription: values.metaDescription as string | undefined,
      keywords: (values.keywords as string[]) || [],
    },
  };
}

/** PATCH body: same fields as create except `storeId` (store is immutable for vendors). */
export function buildUpdateListingBody(values: Record<string, unknown>): Record<string, unknown> {
  const body = buildCreateListingBody(values);
  delete body.storeId;
  return body;
}

type ApiListing = {
  listingType?: string;
  itemType?: string;
  title?: string;
  description?: string;
  category?: unknown;
  subcategory?: unknown;
  store?: unknown;
  price?: number | { amount?: number; currency?: string; priceType?: string; showPrice?: boolean };
  productDetails?: {
    brand?: string;
    model?: string;
    condition?: string;
    yearOfPurchase?: number;
    warranty?: { available?: boolean; duration?: string; details?: string };
  };
  serviceDetails?: {
    serviceType?: string;
    duration?: string;
    availability?: string;
    serviceArea?: string[];
  };
  location?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    showExactLocation?: boolean;
  };
  tags?: string[];
  status?: string;
  expiresAt?: string;
  seo?: { metaTitle?: string; metaDescription?: string; keywords?: string[] };
};

function idOf(ref: unknown): string | undefined {
  if (ref == null) return undefined;
  if (typeof ref === 'string') return ref;
  if (typeof ref === 'object' && ref !== null && '_id' in ref) return String((ref as { _id: string })._id);
  return undefined;
}

export function mapListingToFormValues(listing: ApiListing): Record<string, unknown> {
  const price = listing.price;
  const amount = typeof price === 'number' ? price : price?.amount;
  const currency = typeof price === 'object' && price ? price.currency : 'INR';
  const priceType = typeof price === 'object' && price ? price.priceType : 'negotiable';
  const showPrice = typeof price === 'object' && price ? price.showPrice !== false : true;

  return {
    listingType: listing.listingType || 'product',
    itemType: listing.itemType || 'new',
    title: listing.title,
    description: listing.description,
    category: idOf(listing.category),
    subcategory: idOf(listing.subcategory),
    store: idOf(listing.store),
    price: amount,
    currency,
    priceType,
    showPrice,
    brand: listing.productDetails?.brand,
    model: listing.productDetails?.model,
    condition: listing.productDetails?.condition,
    yearOfPurchase: listing.productDetails?.yearOfPurchase,
    warrantyAvailable: listing.productDetails?.warranty?.available || false,
    warrantyDuration: listing.productDetails?.warranty?.duration,
    warrantyDetails: listing.productDetails?.warranty?.details,
    serviceType: listing.serviceDetails?.serviceType,
    duration: listing.serviceDetails?.duration,
    availability: listing.serviceDetails?.availability,
    serviceArea: listing.serviceDetails?.serviceArea || [],
    address: listing.location?.address,
    city: listing.location?.city,
    state: listing.location?.state,
    zipCode: listing.location?.zipCode,
    showExactLocation: listing.location?.showExactLocation || false,
    tags: listing.tags || [],
    status: listing.status || 'draft',
    expiresAt: listing.expiresAt ? dayjs(listing.expiresAt) : undefined,
    metaTitle: listing.seo?.metaTitle,
    metaDescription: listing.seo?.metaDescription,
    keywords: listing.seo?.keywords || [],
  };
}

export function imageUrlFromListingImage(img: unknown): string {
  if (typeof img === 'string') return img;
  if (typeof img === 'object' && img !== null && 'url' in img) return String((img as { url: string }).url);
  return '';
}

/** Plain text from listing description HTML (for min-length validation with rich text). */
export function listingDescriptionPlainText(html: string): string {
  if (!html || typeof html !== 'string') return '';
  const noTags = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ');
  return noTags.replace(/\s+/g, ' ').trim();
}
